import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import {
  HelpCircle,
  Search,
  ChevronDown,
  Mail,
  AlertTriangle,
  LifeBuoy,
} from "lucide-react";
import {
  FAQ_ARTICLES,
  FAQ_CATEGORIES,
  type FaqCategoryId,
} from "@/data/faq";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { FAQ_CITATIONS } from "@/data/citations";
import { SourcesList } from "@/components/blog/SourcesList";
import { useHideCommerce } from "@/lib/native-app";

export const Route = createFileRoute("/faq")({
  // The question list is fetched when the FAQ page opens, so its text is not
  // part of every other page's download.
  loader: async () => {
    const { FAQ_ARTICLES: articles } = await import("@/data/faq");
    return {
      faqLd: articles.slice(0, 20).map((a) => ({
        "@type": "Question",
        name: a.q,
        url: `https://recoverytrauma.com/faq#faq-panel-${a.id}`,
        acceptedAnswer: {
          "@type": "Answer",
          text: a.a,
          url: `https://recoverytrauma.com/faq#faq-panel-${a.id}`,
          ...(FAQ_CITATIONS[a.id]
            ? { citation: FAQ_CITATIONS[a.id].map((c) => ({ "@type": "WebPage", name: c.title, url: c.url, publisher: c.publisher })) }
            : {}),
        },
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: "FAQ — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Answers to frequently asked questions about Recovery Trauma™ — CPTSD, PTSD, the trauma-informed approach, membership, billing, devices, and safety.",
      },
      { property: "og:title", content: "FAQ — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Answers to common questions about Recovery Trauma™ — trauma recovery, CPTSD, membership, and safety.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/faq" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/faq" }],
    scripts: loaderData
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: loaderData.faqLd,
            }),
          },
        ]
      : [],
  }),
  component: FaqPage,
});


function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlight(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) return text;
  const re = new RegExp(`(${escapeRegExp(q)})`, "ig");
  const parts = text.split(re);
  return parts.map((part, i) =>
    re.test(part) ? (
      <mark
        key={i}
        className="rounded bg-gold/30 px-0.5 text-foreground"
      >
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

const CRISIS_KEYWORDS = [
  "crisis",
  "emergency",
  "suicide",
  "suicidal",
  "self harm",
  "self-harm",
  "harm myself",
  "hurt myself",
  "kill",
  "danger",
  "panic",
  "overdose",
  "help me",
  "urgent",
];

function isCrisisQuery(q: string) {
  const s = q.trim().toLowerCase();
  if (!s) return false;
  return CRISIS_KEYWORDS.some((k) => s.includes(k));
}

/** Answers that describe paying — hidden inside the native app. */
const PAYMENT_TEXT = /payment|checkout|stripe|paypal|billing|price|refund|subscription fee/i;

/**
 * Billing answers that must stay visible inside the native app because they
 * only explain Apple-managed subscriptions, entitlement and cancellation —
 * never external purchasing.
 */
const NATIVE_SAFE_BILLING = new Set([
  "cancel-apple",
  "cancel-keeps-access",
  "restore-purchases",
  "duplicate-subscription",
  "where-did-i-subscribe",
]);

function FaqPage() {
  const [query, setQuery] = useState("");
  const [manualOpen, setManualOpen] = useState<Set<string>>(new Set());
  const [activeCat, setActiveCat] = useState<FaqCategoryId | "all">("all");
  // Inside the native app there is no purchasing at all, so payment answers
  // (and their category filter) are not shown. Unchanged on the website.
  const hideCommerce = useHideCommerce();

  const categories = useMemo(
    () => FAQ_CATEGORIES,
    [hideCommerce],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return FAQ_ARTICLES.filter((a) => {
      if (hideCommerce && !NATIVE_SAFE_BILLING.has(a.id)) {
        if (a.category === "billing") return false;
        if (PAYMENT_TEXT.test(`${a.q} ${a.a}`)) return false;
      }
      const matchCat = activeCat === "all" || a.category === activeCat;
      const matchQ =
        !q || a.q.toLowerCase().includes(q) || a.a.toLowerCase().includes(q);
      return matchCat && matchQ;
    });
  }, [query, activeCat, hideCommerce]);

  const showCrisisCallout = isCrisisQuery(query);

  const isOpen = (id: string) =>
    query.trim().length > 0 ? true : manualOpen.has(id);

  const toggle = (id: string) => {
    setManualOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };


  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      {/* Pinned crisis support link */}
      <div className="sticky top-2 z-30 mb-6">
        <Link
          to="/crisis"
          className="flex items-center justify-between gap-3 rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive shadow-sm backdrop-blur transition-colors hover:bg-destructive/15"
          aria-label="Crisis resources — get immediate help"
        >
          <span className="inline-flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              <span className="font-semibold">Crisis Resources</span>
              <span className="hidden text-destructive/80 sm:inline">
                {" "}— if you need immediate help, tap here.
              </span>
            </span>
          </span>
          <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden="true" />
        </Link>
      </div>

      <header className="mb-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <HelpCircle className="h-3.5 w-3.5" /> Frequently Asked Questions
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Answers, at a glance
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">
          Search common questions or browse by topic. Can't find your answer?
          Visit{" "}
          <Link to="/support" className="text-gold underline-offset-2 hover:underline">
            Support
          </Link>
          .
        </p>
      </header>

      <ComplianceNotice className="mb-6" />



      <div className="relative mb-6">
        <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="🔍 Search FAQs…"
          aria-label="Search frequently asked questions"
          className="w-full rounded-full border border-border bg-card py-3 ps-11 pe-4 text-sm text-foreground placeholder:text-muted-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      <div className="mb-8 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveCat("all")}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            activeCat === "all"
              ? "border-gold bg-gold text-white"
              : "border-border bg-card text-foreground/75 hover:bg-accent"
          }`}
        >
          All topics
        </button>
        {categories.map((c) => {
          const Icon = c.icon;
          const active = activeCat === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveCat(c.id)}
              className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                active
                  ? "border-gold bg-gold text-white"
                  : "border-border bg-card text-foreground/75 hover:bg-accent"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {c.label}
            </button>
          );
        })}
      </div>

      {showCrisisCallout && (
        <div
          role="alert"
          className="mb-4 rounded-2xl border border-destructive/40 bg-destructive/10 p-4 text-sm text-foreground shadow-sm"
        >
          <p className="font-semibold text-destructive">
            🚨 If you need immediate support
          </p>
          <p className="mt-1 text-foreground/85">
            Recovery Trauma™ is not a crisis service. If you are in immediate
            danger, thinking about harming yourself, or need urgent help,
            please contact your local emergency services, crisis line, doctor,
            or mental health professional right now.
          </p>
          <Link
            to="/crisis"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground shadow-sm transition-colors hover:bg-destructive/90"
          >
            <LifeBuoy className="h-4 w-4" aria-hidden="true" />
            Open crisis resources
          </Link>
        </div>
      )}

      {query.trim() && (
        <p
          className="mb-3 text-xs text-muted-foreground"
          role="status"
          aria-live="polite"
        >
          {filtered.length === 0
            ? "No matches yet — try different words, or email info@recoverytrauma.com."
            : `${filtered.length} ${filtered.length === 1 ? "match" : "matches"} for “${query.trim()}”`}
        </p>
      )}


      <ul className="space-y-2">
        {filtered.map((a) => {
          const open = isOpen(a.id);
          const isCrisis = a.id === "crisis";
          return (
            <li
              key={a.id}
              className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
            >
              <button
                type="button"
                onClick={() => toggle(a.id)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-start transition-colors hover:bg-accent/30"
                aria-expanded={open}
                aria-controls={`faq-panel-${a.id}`}
              >
                <span className="text-sm font-medium text-foreground">
                  {isCrisis && (
                    <span aria-hidden="true" className="me-1.5">🚨</span>
                  )}
                  {highlight(a.q, query)}
                </span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-300 ${
                    open ? "rotate-180" : ""
                  }`}
                  aria-hidden="true"
                />
              </button>
              {/* Smooth open/close via grid-rows trick */}
              <div
                id={`faq-panel-${a.id}`}
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                  open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
                aria-hidden={!open}
              >
                <div className="overflow-hidden">
                  <div className="border-t border-border bg-background/40 px-5 py-4 text-sm leading-relaxed text-foreground/85">
                    {isCrisis ? (
                      <CrisisAnswer query={query} />
                    ) : (
                      highlight(a.a, query)
                    )}
                    {FAQ_CITATIONS[a.id] && (
                      <SourcesList compact heading="Sources" sources={FAQ_CITATIONS[a.id]} />
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
        {filtered.length === 0 && (
          <li className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-sm text-muted-foreground">
            No matches. Try a different search or{" "}
            <Link to="/support" className="text-gold underline">
              contact support
            </Link>
            .
          </li>
        )}
      </ul>

      <div className="mt-10 rounded-2xl border border-gold/30 bg-gold/5 p-6 text-center">
        <h2 className="font-serif text-xl text-foreground">
          Still need help?
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
          Contact our support team and we'll get back to you as soon as we can —
          gentle, honest, and trauma-informed, usually within 1–2 business days.
        </p>
        <Link
          to="/support"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[var(--gold-deep)]"
        >
          <Mail className="h-4 w-4" aria-hidden="true" /> Contact Support
        </Link>
      </div>
    </div>
  );
}

function CrisisAnswer({ query }: { query: string }) {
  return (
    <div className="space-y-3">
      <p className="font-semibold text-foreground">
        🚨 {highlight("Need Immediate Help?", query)}
      </p>
      <p>
        {highlight(
          "Recovery Trauma™ is not a crisis service.",
          query,
        )}
      </p>
      <p>
        {highlight(
          "If you are in immediate danger, experiencing a mental health crisis, or thinking about harming yourself, please reach out for support right now.",
          query,
        )}
      </p>
      <Link
        to="/crisis"
        className="mt-1 inline-flex items-center gap-2 rounded-full bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground shadow-sm transition-colors hover:bg-destructive/90"
      >
        <LifeBuoy className="h-4 w-4" aria-hidden="true" />
        Click here for Crisis Resources
      </Link>
      <p className="text-xs text-muted-foreground">
        Includes international helplines, emergency numbers, Samaritans, 988
        Lifeline, FindAHelpline, and local emergency services.
      </p>
    </div>
  );
}
